<template>
  <div class="schedule-details-form">
    <validation-observer
      ref="scheduleFormValidation"
      class="h-100 d-flex flex-column"
    >
      <b-form class="flex-grow-1 d-flex flex-column">
        <b-form-group
          :label="$t('field.title')"
          label-for="schedule-title"
        >
          <b-form-input
            id="schedule-title"
            v-model="form.title"
            :placeholder="$t('placeholder.title')"
            trim
          />
        </b-form-group>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  // BFormTextarea,
} from 'bootstrap-vue'

export default {
  name: 'ScheduleDetailsForm',
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    // BFormTextarea,
  },
  props: {
    initialFormData: {
      type: Object,
      default: () => ({
        title: '',
        // description: '',
      }),
    },
    timeShifts: {
      type: Array,
      default: () => [],
    },
    selectedDate: {
      type: Date,
      default: null,
    },
    events: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: { ...this.initialFormData },
    }
  },
  computed: {
    isValid() {
      return this.form.title.trim() !== ''
    },
  },
  watch: {
    form: {
      handler(newVal) {
        this.$emit('update:form', newVal)
      },
      deep: true,
    },
  },
  methods: {
    formatTime(timeString) {
      if (!timeString) return ''

      try {
        // Handle ISO string format
        if (typeof timeString === 'string' && timeString.includes('T')) {
          const date = new Date(timeString)
          return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
        }

        // Handle HH:MM format
        if (typeof timeString === 'string' && timeString.includes(':')) {
          return timeString
        }

        return timeString
      } catch (error) {
        console.error('Error formatting time:', error)
        return timeString
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''

      try {
        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) return dateString

        return date.toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      } catch (error) {
        console.error('Error formatting date:', error)
        return dateString
      }
    },
    getRepeatLabel(repeat) {
      const repeatLabels = {
        none: this.$t('field.none'),
        daily: this.$t('field.everyDay'),
        weekly: this.$t('field.everyWeek'),
        monthly: this.$t('field.everyMonth'),
        yearly: this.$t('field.everyYear'),
        custom: this.$t('field.custom'),
      }

      return repeatLabels[repeat] || repeat
    },
    getLabel(repeat) {
      return this.getRepeatLabel(repeat)
    },
    getFrequencyLabel(frequency) {
      const frequencyLabels = {
        0: this.$t('field.single'),
        1: this.$t('field.everyDay'),
        2: this.$t('field.everyWeek'),
        3: this.$t('field.everyMonth'),
        4: this.$t('field.everyYear'),
      }

      return frequencyLabels[frequency] || this.$t('field.custom')
    },
    getIntervalUnit(frequency) {
      const intervalUnits = {
        0: '',
        1: this.$t('field.day'),
        2: this.$t('field.week'),
        3: this.$t('field.month'),
        4: this.$t('field.year'),
      }

      return intervalUnits[frequency] || ''
    },
    getRecurrenceDetails(apiEvent) {
      if (!apiEvent || apiEvent.frequency === 0) return null

      // Weekly recurrence - show days of week
      if (apiEvent.frequency === 2 && apiEvent.daysOfWeek && apiEvent.daysOfWeek.length > 0) {
        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
        const days = apiEvent.daysOfWeek.map(day => this.$t(`field.${dayNames[day].toLowerCase()}`)).join(', ')
        return `${this.$t('field.on')} ${days}`
      }

      // Monthly recurrence - show days of month
      if (apiEvent.frequency === 3 && apiEvent.daysOfMonth && apiEvent.daysOfMonth.length > 0) {
        if (apiEvent.daysOfMonth.length === 1) {
          return `${this.$t('field.onDay')} ${apiEvent.daysOfMonth[0]}`
        }
        return `${this.$t('field.onDays')} ${apiEvent.daysOfMonth.join(', ')}`
      }

      // Yearly recurrence - show months
      if (apiEvent.frequency === 4 && apiEvent.monthsOfYear && apiEvent.monthsOfYear.length > 0) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        const months = apiEvent.monthsOfYear.map(month => this.$t(`field.${monthNames[month - 1].toLowerCase()}`)).join(', ')
        return `${this.$t('field.in')} ${months}`
      }

      return null
    },
    getUniqueTimeShifts(events) {
      if (!events || !Array.isArray(events)) return []

      const seen = new Set()

      return events.filter(event => {
        if (!event || !event.timeShiftId) return false

        // Create a unique key based on timeShiftId and frequency
        const key = `${event.timeShiftId}_${event.frequency}`

        if (!seen.has(key)) {
          seen.add(key)
          return true
        }
        return false
      })
    },
    getTimeShiftTitle(apiEvent) {
      if (!apiEvent) return ''

      // If the event has a direct timeShift object
      if (apiEvent.timeShift && apiEvent.timeShift.title) {
        return apiEvent.timeShift.title
      }

      // If the event has a timeShiftId, look it up in the timeShifts array
      if (apiEvent.timeShiftId) {
        const timeShift = this.timeShifts.find(ts => ts.id === apiEvent.timeShiftId)
        return timeShift ? timeShift.title : 'Untitled Time Shift'
      }

      return 'Untitled Time Shift'
    },
  },
}
</script>

<style lang="scss" scoped>
.schedule-details-form {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  :deep(.form-group) {
    margin-bottom: 1.5rem;
  }

  :deep(.form-control) {
    width: 100%;
  }

  :deep(textarea.form-control) {
    resize: none;
    height: auto;
  }
}

.time-shift-info {
  h6 {
    color: #5e5873;
    font-weight: 500;
    margin-bottom: 1rem;
  }
}

.time-shift-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
}

.time-shift-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  background-color: #f8f8f8;
  border-radius: 0.428rem;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.time-shift-marker {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: var(--primary);
  border-top-left-radius: 0.428rem;
  border-bottom-left-radius: 0.428rem;
}

.time-shift-title {
  flex: 1;
  font-weight: 500;
  color: #5e5873;
  font-size: 0.95rem;
  margin-right: 1.5rem;
  padding-left: 0.5rem;
}

.time-shift-repeat {
  font-size: 0.75rem;
  color: var(--primary);
  background-color: rgba(115, 103, 240, 0.12);
  padding: 0.25rem 0.75rem;
  border-radius: 1.5rem;
  white-space: nowrap;
  font-weight: 500;
}

.no-events-message {
  color: #b9b9c3;
  font-style: italic;
  text-align: center;
  padding: 1rem;
  background-color: #f8f8f8;
  border-radius: 0.428rem;
  border: 1px dashed #ebe9f1;
}
</style>
